import React, { ReactNode } from "react";
import {
  Box,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import Card from "@/components/Card";
import { StyledBadge, StyledIconButton } from "@/components/styled";
import { getFileStorageBadgeVariant } from "@/utils/getIncentiveBadgeVariant.ts";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter.ts";
import { getEnrollmentBadgeVariant } from "@/utils/getEnrollmentBadgeVariant.ts";
import { useProjectsStore } from "@/store/projectsStore.ts";
import { formatCurrency } from "@/utils/formatCurrency.ts";
import { removeTrailZero } from "@/utils/numberFormatting";
import BadgeSelect from "@/pages/admin-pages/UserManagementPage/components/BadgeSelect.tsx";
import { INCENTIVE_STATUS_ITEMS } from "@/constants";
import { replaceUnderscoresWithSpaces } from "@/utils/replaceUnderscoreWithSpaces.ts";
import SVGGoTo from "@/assets/icons/SVGGoTo.tsx";
import { downloadFile } from "@/utils/downloadFile.ts";
import { useAuthStore } from "@/store/authStore.ts";
import { calculateUsableBatteryCapacity } from "@/utils/calculateUsableBatteryCapacity.ts";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  getContractorName,
  getProjectType,
  getApplicantState,
  getCoCCareFera,
  getLdcAccountNumber,
} from "@/utils/projectUtils.ts";

export const BoxInfoWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "20px",
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: "16px",
  },
}));

const FinanceInfoCard = () => {
  const projectDetails = useProjectsStore((state) => state.projectDetails);
  const role = useAuthStore((state) => state.role);
  const navigate = useNavigate();

  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));

  const PLACEHOLDER = "—";

  const handleBack = () => {
    navigate("/finance");
  };

  const details = projectDetails as any;

  // Helper function to get contractor name with placeholder
  const getContractorNameWithPlaceholder = () => {
    const name = getContractorName(details);
    return name || PLACEHOLDER;
  };

  // Helper function to get payee information
  const getPayee = () => {
    return details?.payee || getContractorNameWithPlaceholder();
  };

  const getPayeeType = () => {
    return details?.payeeType || "Contractor";
  };

  // Helper function to get applicant state with placeholder
  const getApplicantStateWithPlaceholder = () => {
    const state = getApplicantState(details);
    return state || PLACEHOLDER;
  };

  // Helper function to get additional fields not in ProjectRecord type
  const getField = (fieldName: string) => {
    const details = projectDetails as any;
    return details?.[fieldName] || PLACEHOLDER;
  };

  // Helper function to get new solar system size
  const getNewSolarSystemSize = () => {
    const kwdc = projectDetails?.newSolarKwdc || projectDetails?.solarKwdc;
    return kwdc ? `${kwdc} kW rooftop` : PLACEHOLDER;
  };

  // Helper function to get NEM/SBP value
  const getNemSbp = () => {
    const details = projectDetails as any;
    return details?.sdcpProject?.NEM_SBP || PLACEHOLDER;
  };

  // Helper function to format dates consistently
  const formatDate = (dateField: string) => {
    const dateValue = getField(dateField);
    return dateValue !== PLACEHOLDER
      ? dayjs(dateValue).format("MMMM D, YYYY")
      : PLACEHOLDER;
  };

  // Helper function to get LDC Account Number with placeholder
  const getLdcAccountNumberWithPlaceholder = () => {
    const accountNumber = getLdcAccountNumber(details);
    return accountNumber || PLACEHOLDER;
  };

  // Helper function to format CoC/Care/Fera status
  const getCoCCareFeraFormatted = () => {
    const { coc, care, fera } = getCoCCareFera(details);
    return `${coc}/${care}/${fera}`;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Card sx={{ position: "relative" }}>
        {/* Row 1 - As per Figma: ID, Applicant Name, Applicant Address, Applicant Email, Applicant City, Applicant State, Applicant Zip, Contractor, Payee, Payee Type, LDC Account Number */}
        <BoxInfoWrapper sx={{ overflow: "hidden" }}>
          <FinanceInfoItem title={"ID"}>
            {projectDetails?.id || PLACEHOLDER}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Applicant Name"}>
            {projectDetails?.customerName || PLACEHOLDER}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Applicant Address"}>
            {projectDetails?.address || PLACEHOLDER}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Applicant Email"}>
            {projectDetails?.accountEmail || PLACEHOLDER}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Applicant City"}>
            {projectDetails?.city || PLACEHOLDER}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Applicant State"}>
            {getApplicantStateWithPlaceholder()}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Applicant Zip"}>
            {projectDetails?.zipCode || PLACEHOLDER}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Contractor"}>
            {getContractorNameWithPlaceholder()}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Payee"}>{getPayee()}</FinanceInfoItem>
          <FinanceInfoItem title={"Payee Type"}>
            {getPayeeType()}
          </FinanceInfoItem>
          <FinanceInfoItem title={"LDC Account Number"}>
            {getLdcAccountNumberWithPlaceholder()}
          </FinanceInfoItem>
        </BoxInfoWrapper>

        {/* Row 2 - As per Figma: NEM/SBP, New Solar System Size Installed, Battery Manufacturer, Battery Product, Battery S/N, # of Batteries, Battery Nameplate kW, Battery Usable Capacity, PTO Nameplate kW, PTO Solar kW-AC */}
        <BoxInfoWrapper sx={{ marginTop: "40px", overflow: "hidden" }}>
          <FinanceInfoItem title={"NEM/SBP"}>{getNemSbp()}</FinanceInfoItem>
          <FinanceInfoItem title={"New Solar System Size Installed"}>
            {getNewSolarSystemSize()}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Battery Manufacturer"}>
            {projectDetails?.batteryOem || PLACEHOLDER}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Battery Product"}>
            {projectDetails?.batteryModel ||
              projectDetails?.batteryType ||
              PLACEHOLDER}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Battery S/N"}>
            {getField("batterySerialNumber")}
          </FinanceInfoItem>
          <FinanceInfoItem title={"# of Batteries"}>
            {projectDetails?.numberOfBatteries || PLACEHOLDER}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Battery Nameplate kW"}>
            {getField("batteryNameplateKw")}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Battery Usable Capacity"}>
            {projectDetails?.usableCapacity
              ? `${removeTrailZero(projectDetails.usableCapacity)} kWh`
              : PLACEHOLDER}
          </FinanceInfoItem>
          <FinanceInfoItem title={"PTO Nameplate kW"}>
            {getField("ptoNameplateKw")}
          </FinanceInfoItem>
          <FinanceInfoItem title={"PTO Solar kW-AC"}>
            {getField("ptoSolarKwAc")}
          </FinanceInfoItem>
          <FinanceInfoItem title={"CoC/Care/Fera"}>
            {getCoCCareFeraFormatted()}
          </FinanceInfoItem>
        </BoxInfoWrapper>

        {/* Row 3 - As per Figma: Type, Upfront Incentive Amount, Eligible Project Cost, T&Cs, PTO Letter Date, PTO Submitted Date, Date Moved to Finance Review, Notes */}
        <BoxInfoWrapper sx={{ marginTop: "40px", overflow: "hidden" }}>
          <FinanceInfoItem title={"Type"}>
            {getProjectType(details)}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Upfront Incentive Amount"}>
            {projectDetails?.upfrontIncentivePayment
              ? formatCurrency(
                  Number(projectDetails?.upfrontIncentivePayment) / 100
                )
              : PLACEHOLDER}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Eligible Project Cost"}>
            {getField("total_cost")
              ? formatCurrency(Number(getField("total_cost")))
              : PLACEHOLDER}
          </FinanceInfoItem>
          <FinanceInfoItem title={"T&Cs"}>
            {getField("termsAndConditions")}
          </FinanceInfoItem>
          <FinanceInfoItem title={"PTO Letter Date"}>
            {formatDate("ptoLetterDate")}
          </FinanceInfoItem>
          <FinanceInfoItem title={"PTO Submitted Date"}>
            {formatDate("ptoSubmittedDate")}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Date Moved to Finance Review"}>
            {formatDate("finance_review_date")}
          </FinanceInfoItem>
          <FinanceInfoItem title={"Notes"}>
            {getField("finance_notes") || projectDetails?.note || PLACEHOLDER}
          </FinanceInfoItem>
        </BoxInfoWrapper>
      </Card>
    </Box>
  );
};

export default FinanceInfoCard;

const FinanceInfoItem = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Typography
        variant={"body2Small"}
        sx={{ color: "textLightGray3", whiteSpace: "nowrap" }}
      >
        {title}
      </Typography>
      {typeof children === "string" ? (
        <Typography
          variant={"body2"}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
            fontWeight: 600,
          }}
        >
          {children}
        </Typography>
      ) : (
        children
      )}
    </Box>
  );
};
